import React from "react";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";

const TIME_FORMAT = "HH:mm";

/**
 * 24-hour time picker whose value is kept as an "HH:mm" string.
 *
 * Wraps MUI X `DesktopTimePicker` so the popup shows scrollable hour/minute
 * columns (the `MultiSectionDigitalClock` view). The component converts
 * between the "HH:mm" string held in form state and the moment object MUI
 * works with, so the value persisted/submitted is unchanged (e.g. "08:00").
 *
 * @param {object} props - Component props.
 * @param {string} props.value - Current time as an "HH:mm" string (e.g. "08:00"). Empty string renders no selection.
 * @param {(value: string) => void} props.onChange - Called with the new "HH:mm" string ("" when cleared/invalid).
 * @param {boolean} [props.disabled=false] - Whether the field is disabled.
 * @returns {JSX.Element} The time picker field.
 */
export function TimeField({ value, onChange, disabled = false }) {
  const selected = value ? moment(value, TIME_FORMAT) : null;

  /**
   * Convert the moment value emitted by the picker back to an "HH:mm" string.
   *
   * @param {import("moment").Moment | null} next - New value from the picker.
   * @returns {void}
   */
  const handleChange = (next) => {
    onChange(next && next.isValid() ? next.format(TIME_FORMAT) : "");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DesktopTimePicker
        value={selected && selected.isValid() ? selected : null}
        onChange={handleChange}
        ampm={false}
        views={["hours", "minutes"]}
        disabled={disabled}
        slotProps={{ textField: { size: "small", fullWidth: true } }}
      />
    </LocalizationProvider>
  );
}

export default TimeField;
